import {
  CheckboxTree,
  CheckboxTreeOptionType,
  FormControl,
  Search,
  Separator,
} from 'platform/components';
import {Box, Stack, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useController} from 'react-hook-form';

import {isNil, isNotNil, sum} from 'ramda';
import {ceil, isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../hooks/useCatalogue';
import {GetAllEnumsResponse} from '../../../types/api/api';
import {VehicleFormType} from '../types/VehicleFormType';

interface AllFeaturesFiledProps extends TestIdProps {
  control: FormControl<VehicleFormType>;
}

export function AllFeaturesFiled(props: AllFeaturesFiledProps) {
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const {field} = useController({
    name: 'features',
    control: props.control,
  });
  const {features} = useCatalogue();

  const handleChange = (value: string) => {
    if (isNil(field.value)) {
      field.onChange([value]);
      props.control.onChange();
      return;
    }

    const findRootValue = (value: string) => {
      const feature = features.find((feature) => feature.const_key === value);
      const parent = feature?.parent;
      if (!parent) {
        return value;
      }
      return findRootValue(parent);
    };
    const findSelectedValueInTree = (value: string): string | null => {
      const isSelected = field.value?.includes(value);
      if (isSelected) {
        return value;
      }

      const children = features
        .filter((feature) => feature.parent === value)
        .map((feature) => feature.const_key);
      for (const child of children) {
        const result = findSelectedValueInTree(child);
        if (isNotNil(result)) {
          return result;
        }
      }
      return null;
    };

    const isSelected = field.value.includes(value);
    const selectedValueInTree = findSelectedValueInTree(findRootValue(value));

    const newValue = isSelected
      ? field.value?.filter((item) => item !== value)
      : [...field.value, value].filter((item) => item !== selectedValueInTree);

    field.onChange(newValue);
    props.control.onChange();
  };

  const filteredFeatures = searchValue
    ? features.filter((feature) => {
        const label = feature.labels[0]?.label;
        return (
          isNotNil(label) && label.toLocaleUpperCase().includes(searchValue.toLocaleUpperCase())
        );
      })
    : features;

  const treeOptions = getTreeOptions(getFilteredFeaturesWithParents(filteredFeatures, features));

  const [firstOptionsRow, secondOptionsRow] = getRowOptions(treeOptions);

  return (
    <VStack spacing={6} width="100%">
      <Separator spacing={0} />
      <Search
        value={searchValue}
        onChange={setSearchValue}
        data-testid={suffixTestId('featureSearch', props)}
      />
      <Stack direction={['column', 'column', 'row', 'row']} spacing={[2, 2, 4, 4]}>
        <Box flex={1}>
          <CheckboxTree
            key={searchValue}
            value={field.value ?? null}
            onSingleValueChange={handleChange}
            options={firstOptionsRow}
            data-testid={suffixTestId('firstOptionsRow', props)}
          />
        </Box>
        <Box flex={1}>
          <CheckboxTree
            key={searchValue}
            value={field.value ?? null}
            onSingleValueChange={handleChange}
            options={secondOptionsRow}
            data-testid={suffixTestId('secondOptionsRow', props)}
          />
        </Box>
      </Stack>
    </VStack>
  );
}

const getOptionsCount = (items: CheckboxTreeOptionType[]): number =>
  sum(items.map((item) => (item.options?.length ? getOptionsCount(item.options) + 1 : 1)));

const getRowOptions = (items: CheckboxTreeOptionType[]) => {
  const columnOptionsCount = ceil(getOptionsCount(items) / 2);

  const firstRow: CheckboxTreeOptionType[] = [];
  const secondRow: CheckboxTreeOptionType[] = [];
  let currentCount = 0;

  for (const item of items) {
    if (currentCount < columnOptionsCount) {
      firstRow.push(item);
    } else {
      secondRow.push(item);
    }
    currentCount += 1 + getOptionsCount(item.options ?? []);
  }

  return [firstRow, secondRow] as const;
};

const getFilteredFeaturesWithParents = (
  items: GetAllEnumsResponse[number]['keys'],
  allItems: GetAllEnumsResponse[number]['keys']
): GetAllEnumsResponse[number]['keys'] => {
  if (isNilOrEmpty(items)) {
    return [];
  }

  const missingParent = items.filter(
    (item) => !items.some((otherItem) => otherItem.const_key === item.parent)
  );
  const missingParentItems = allItems.filter((item) =>
    missingParent.some((otherItem) => item.const_key === otherItem.parent)
  );

  return items.concat(getFilteredFeaturesWithParents(missingParentItems, allItems));
};

const getTreeOptions = (
  items: GetAllEnumsResponse[number]['keys'],
  id?: string
): CheckboxTreeOptionType[] =>
  items
    ?.filter((item) => item.parent === (id ?? null))
    .map((item) => ({
      label: item.labels[0]?.label ?? item.const_key,
      value: item.const_key,
      options: getTreeOptions(items, item.const_key),
    })) ?? [];
